import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SHELF_RANGE } from '../../constants/app.constants';
import { checkShelfExists, manageShelf } from '../../services/devAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import { invalidateShelfCache } from '../../hooks/useShelfValidation';
import ShelfAutocompleteInput from '../shared/ShelfAutocompleteInput';
import '../labels/PrintBoxLabel.css';
import './ShelfManage.css';

const ShelfManage: React.FC = () => {
  // Select inputs
  const [shelfNumber, setShelfNumber] = useState<string>('');
  const [shelfLetter, setShelfLetter] = useState<string>('');
  const [shelfPosition, setShelfPosition] = useState<string>('');

  // Text input
  const [textInput, setTextInput] = useState<string>('');

  // Which input last changed — determines which value we check
  const [activeInput, setActiveInput] = useState<'select' | 'text' | null>(null);

  // Status
  const [checking, setChecking] = useState<boolean>(false);
  const [exists, setExists] = useState<boolean | null>(null);
  const [resolvedName, setResolvedName] = useState<string>('');

  // Action result
  const [acting, setActing] = useState<boolean>(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getShelfName = useCallback((): string => {
    if (activeInput === 'select') {
      if (!shelfNumber || !shelfLetter || !shelfPosition) return '';
      return `${shelfNumber}-${shelfLetter}-${shelfPosition}`;
    }
    if (activeInput === 'text') {
      return textInput.trim().toUpperCase();
    }
    return '';
  }, [activeInput, shelfNumber, shelfLetter, shelfPosition, textInput]);

  const checkShelf = useCallback(async (name: string) => {
    if (!name) {
      setExists(null);
      setResolvedName('');
      return;
    }
    setChecking(true);
    setResult(null);
    try {
      const response = await checkShelfExists(name);
      setExists(response.exists);
      setResolvedName(response.shelf_name);
    } catch {
      setExists(null);
      setResolvedName('');
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    const name = getShelfName();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!name) {
      setExists(null);
      setResolvedName('');
      return;
    }

    debounceRef.current = setTimeout(() => {
      checkShelf(name);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [getShelfName, checkShelf]);

  const handleSelectChange = (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLSelectElement>): void => {
      setter(e.target.value);
      setActiveInput('select');
      setTextInput('');
      setResult(null);
    };

  const handleTextChange = (value: string): void => {
    setTextInput(value);
    setActiveInput('text');
    setShelfNumber('');
    setShelfLetter('');
    setShelfPosition('');
    setResult(null);
  };

  const handleAction = async (): Promise<void> => {
    if (exists === null || !resolvedName) return;
    const action = exists ? 'delete' : 'create';

    if (action === 'delete') {
      const confirmed = window.confirm(`Delete shelf "${resolvedName}"? This cannot be undone.`);
      if (!confirmed) return;
    }

    setActing(true);
    setResult(null);
    try {
      const response = await manageShelf(resolvedName, action);
      if (response.success) {
        invalidateShelfCache();
        setResult({
          success: true,
          message: action === 'create'
            ? `Shelf "${resolvedName}" created.`
            : `Shelf "${resolvedName}" deleted.`,
        });
        setExists(action === 'create' ? true : false);

        setShowSuccess(true);
        if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = setTimeout(() => {
          setShowSuccess(false);
          setResult(null);
        }, 1000);
      }
    } catch (err: unknown) {
      setResult({ success: false, message: getApiErrorMessage(err, `Failed to ${action} shelf`) });
    } finally {
      setActing(false);
    }
  };

  const shelfName = getShelfName();
  const hasInput = shelfName.length > 0;

  return (
    <div className="pbl-container">
      <div className="pbl-section">
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">Shelf Select</span>
        </div>
        <div className="pbl-dropdown-container">
          <select
            className="pbl-dropdown"
            value={shelfNumber}
            onChange={handleSelectChange(setShelfNumber)}
          >
            <option value="">--</option>
            {SHELF_RANGE.NUMBERS_1_TO_10.map((n) => (
              <option key={n} value={String(n)}>{n}</option>
            ))}
          </select>
          <span className="pbl-separator">-</span>
          <select
            className="pbl-dropdown"
            value={shelfLetter}
            onChange={handleSelectChange(setShelfLetter)}
          >
            <option value="">--</option>
            {SHELF_RANGE.LETTERS_A_TO_Z.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <span className="pbl-separator">-</span>
          <select
            className="pbl-dropdown"
            value={shelfPosition}
            onChange={handleSelectChange(setShelfPosition)}
          >
            <option value="">--</option>
            {SHELF_RANGE.POSITIONS_1_TO_6.map((p) => (
              <option key={p} value={String(p)}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="pbl-section">
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">Text Input</span>
        </div>
        <ShelfAutocompleteInput
          value={textInput}
          onChange={handleTextChange}
          onSelect={handleTextChange}
          placeholder="e.g. 1-A-1 or CUSTOM-SHELF"
        />
      </div>

      {checking && hasInput && (
        <div className="pbl-message">Checking...</div>
      )}

      {!checking && hasInput && exists === true && (
        <div className="sm-status sm-status--exists">
          &quot;{resolvedName}&quot; exists in wms.shelves
        </div>
      )}

      {!checking && hasInput && exists === false && (
        <div className="sm-status sm-status--not-exists">
          &quot;{resolvedName}&quot; does not exist in wms.shelves
        </div>
      )}

      {!checking && hasInput && exists !== null && (
        <button
          className={`sm-action-btn ${exists ? 'sm-action-btn--delete' : 'sm-action-btn--create'}`}
          onClick={handleAction}
          disabled={acting}
        >
          {acting
            ? (exists ? 'Deleting...' : 'Creating...')
            : (exists ? `DELETE "${resolvedName}"` : `CREATE "${resolvedName}"`)
          }
        </button>
      )}

      {result && !result.success && (
        <div className="sm-result sm-result--error">
          {result.message}
        </div>
      )}

      {showSuccess && (
        <div className="sm-success-flash">
          <div className="sm-success-message">&#10003; Verified!</div>
        </div>
      )}
    </div>
  );
};

export default ShelfManage;
